import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ShieldCheck, Lock, Fingerprint, Server } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function SecurityFeature() {
  useSEO({
    title: "Vault-Grade Security",
    description: "Stripe-grade encryption, JWT-secured sessions, Google sign-in, vaulted credentials. Your money lives behind iron.",
    path: "/atelier/security",
  });
  return (
    <main data-testid="atelier-security" className="bg-bg text-white min-h-screen">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <Link to="/" data-testid="atelier-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-fg hover:text-gold transition-colors">
          <ArrowLeft size={14} /> Back to Atelier
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 01 / Atelier · Vault-Grade Security</div>
            <h1 className="font-display text-5xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
              Behind<br/><span className="italic gold-text">iron walls.</span>
            </h1>
            <p className="mt-8 text-secondary-fg text-lg leading-relaxed max-w-xl font-light">
              We treat your sessions like a private bank treats its vault. Card data never touches our servers — it lives with Stripe's PCI-DSS Level 1 infrastructure. Your authentication is signed with rotating keys and hardened cookies.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/register" data-testid="atelier-cta-register" className="btn-gold inline-flex items-center gap-2">Open a secured account <ArrowUpRight size={16} /></Link>
              <Link to="/login" className="btn-ghost">Sign in</Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="glass p-8 rounded-md">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-fg mb-3">Security stack</div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-3"><ShieldCheck size={16} className="text-gold"/> Stripe PCI-DSS Level 1 checkout</li>
                <li className="flex items-center gap-3"><Lock size={16} className="text-gold"/> Bcrypt-hashed passwords (cost 12)</li>
                <li className="flex items-center gap-3"><Fingerprint size={16} className="text-gold"/> JWT bearer tokens, short-lived</li>
                <li className="flex items-center gap-3"><Server size={16} className="text-gold"/> TLS 1.3, HSTS, secure cookies</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border hairline-strong">
          {[
            ["Stripe-grade rails", "Card data is tokenised by Stripe at the browser. We never see, store, or transmit raw PAN."],
            ["JWT sessions", "Short-lived, signed bearer tokens. Stolen cookies expire. No long-term server sessions to hijack."],
            ["Google Sign-In", "Enterprise SSO via Google. Authenticate with passkeys and 2FA — y ℶα inherits the protections."],
            ["Bcrypt passwords", "Passwords hashed with bcrypt cost 12. Rainbow tables and credential dumps are useless."],
            ["TLS 1.3 + HSTS", "Every byte in transit is encrypted. The browser refuses to downgrade."],
            ["Audit trail", "Every authentication, transfer, and link event is logged with timestamps."],
          ].map(([t, b]) => (
            <div key={t} className="bg-bg p-8">
              <div className="flex items-start gap-3">
                <ShieldCheck className="text-gold mt-1 shrink-0" size={18} strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl mb-2">{t}</h3>
                  <p className="text-secondary-fg text-sm leading-relaxed font-light">{b}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 text-center">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
            Quiet. <span className="italic gold-text">Encrypted. Yours.</span>
          </h2>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/register" className="btn-gold inline-flex items-center gap-2">Enter the vault <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
